/**
 * @file LoginPage.tsx
 * @description Страница /login (phase08.5 — shadcn). Email + password + submit.
 *
 * phase19.29: страница входа — в теме Commora по макету login-mockup.png
 * (общая для всех кабинетов, до входа магазин ещё неизвестен): каркас
 * `CommoraAuthShell` (панель бренда слева с 1024px), «Войдите в свой
 * аккаунт», поля с значками и «показать пароль», «Забыли пароль?», «Войти →»
 * — главное действие экрана (градиент бренда), «Соединение защищено».
 * Не рисуем то, чего нет: вход через Google, регистрацию, «Запомнить
 * меня», выбор языка. Логика входа не менялась.
 */

import { ArrowRight, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link as ReactRouterLink, Navigate, useLocation } from "react-router";

import { CommoraAuthShell } from "../../components/brand/CommoraAuthShell";
import { LoginEmailField, LoginPasswordField } from "../../components/brand/LoginCredentialFields";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { landingPathFor } from "../../auth/format";
import { useAuth } from "../../auth/hooks";
import type { MeResponse } from "../../auth/types";
import { ApiError } from "../../lib/api-client";

export function LoginPage() {
  const { status, me, login } = useAuth();
  const location = useLocation();
  const fromState = location.state as { from?: string } | null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated" && me) {
    return <Navigate to={resolveTarget(me, fromState?.from)} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email: email.trim(), password });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Неизвестная ошибка. Повторите попытку.",
      );
      setSubmitting(false);
    }
  }

  return (
    <CommoraAuthShell tabTitle="Вход">
      <form onSubmit={onSubmit} noValidate className="flex w-full flex-col gap-5">
        <h1 className="type-section-title text-center text-on-surface-variant">Войдите в свой аккаунт</h1>

        <LoginEmailField value={email} onChange={setEmail} disabled={submitting} />
        <LoginPasswordField value={password} onChange={setPassword} disabled={submitting} />

        <div className="-mt-2 flex justify-end">
          <ReactRouterLink
            to="/reset/request"
            className="inline-flex min-h-12 items-center text-sm font-medium text-primary hover:underline"
          >
            Забыли пароль?
          </ReactRouterLink>
        </div>

        {/* Ошибка — текстом под формой, не только цветом. */}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Button type="submit" variant="brand" loading={submitting} className="w-full">
          {submitting ? "Входим…" : "Войти"}
          {submitting ? null : <ArrowRight className="size-4" aria-hidden />}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-xs text-on-surface-variant">
          <ShieldCheck className="size-4" aria-hidden />
          Соединение защищено
        </p>
      </form>
    </CommoraAuthShell>
  );
}

function resolveTarget(me: MeResponse, from?: string): string {
  if (from) return from;
  if (me.activeContext) return landingPathFor(me.activeContext);
  if (me.contexts.length === 0) return "/no-context";
  return "/select-context";
}
